import {defineStore} from 'pinia';
import {useThemeStore} from "@js/store/ThemeStore.js";
import baseImg from '@img/callback/base.webp'
import {checkDocument} from "@js/helpers.ts";
import axios from "axios";

const getBaseFormDefaultData = () => ({
    title: 'Обратный звонок',
    description: 'Оставьте заявку,\n мы вам перезвоним',
    bitrix_title: 'Заявка с сайта X-DEVS',
    img_src: baseImg,
    data: {
        full_name: '',
        phone: '',
        captcha_token: '',
        comment: '',
    },
    files: [],
    file_previews: [],
    errors: [],
    isOpen: false,
    animation_in_progress: false,
    sending: false,
    successfully_sent: false,
})

export const useCallbackStore = defineStore('callbackStore', {
    state: () => ({
        base_form: getBaseFormDefaultData()
    }),

    actions: {
        clearFormFilePreviews(form) {
            if (!form?.file_previews) return

            form.file_previews.forEach((url) => {
                if (!url) return
                try {
                    URL.revokeObjectURL(url)
                } catch (e) {
                }
            })

            form.file_previews = []
        },

        addFilesToForm(form, files = []) {
            if (!form) return

            const maxFiles = 10
            const maxFileSize = 10 * 1024 * 1024 // 10 MB
            const maxTotalSize = 50 * 1024 * 1024 // 50 MB

            const allowedMimeTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/webp',
                'image/heic',
                'image/heif'
            ]

            const allowedExtensions = /\.(jpg|jpeg|png|webp|heic|heif)$/i

            const nextFiles = [...(form.files || [])]
            const nextPreviews = [...(form.file_previews || [])]
            const errors = {}

            let totalSize = nextFiles.reduce((sum, file) => sum + (file.size || 0), 0)

            files.forEach((file) => {
                if (nextFiles.length >= maxFiles) {
                    if (!errors.files) errors.files = []
                    errors.files.push(`Можно загрузить не более ${maxFiles} фото.`)
                    return
                }

                const isValidType =
                    allowedMimeTypes.includes(file.type) ||
                    allowedExtensions.test(file.name)

                if (!isValidType) {
                    if (!errors.files) errors.files = []
                    errors.files.push(`Файл "${file.name}" должен быть изображением.`)
                    return
                }

                if (file.size > maxFileSize) {
                    if (!errors.files) errors.files = []
                    errors.files.push(`Файл "${file.name}" превышает 10 МБ.`)
                    return
                }

                if (totalSize + file.size > maxTotalSize) {
                    if (!errors.files) errors.files = []
                    errors.files.push(`Общий размер фото не должен превышать 50 МБ.`)
                    return
                }

                nextFiles.push(file)
                nextPreviews.push(URL.createObjectURL(file))
                totalSize += file.size
            })

            form.files = nextFiles
            form.file_previews = nextPreviews

            if (Object.keys(errors).length) {
                form.errors = {
                    ...(form.errors || {}),
                    ...errors,
                }
            } else if (form.errors?.files) {
                const nextErrors = {...form.errors}
                delete nextErrors.files
                form.errors = nextErrors
            }
        },

        removeFormFile(form, index) {
            if (!form) return

            const preview = form.file_previews?.[index]

            if (preview) {
                try {
                    URL.revokeObjectURL(preview)
                } catch (e) {
                }
            }

            form.files?.splice(index, 1)
            form.file_previews?.splice(index, 1)

            if (form.errors?.files) {
                const nextErrors = {...form.errors}
                delete nextErrors.files
                form.errors = nextErrors
            }
        },

        clearBaseFormFilePreviews() {
            this.clearFormFilePreviews(this.base_form)
        },

        addBaseFormFiles(files = []) {
            this.addFilesToForm(this.base_form, files)
        },

        removeBaseFormFile(index) {
            this.removeFormFile(this.base_form, index)
        },

        resetBaseFormForOpen(data = {}) {
            this.clearBaseFormFilePreviews()

            const defaults = getBaseFormDefaultData()

            this.base_form.title = data.title ?? defaults.title
            this.base_form.description = data.description ?? defaults.description
            this.base_form.bitrix_title = data.bitrix_title ?? defaults.bitrix_title
            this.base_form.img_src = data.img_src ?? defaults.img_src

            this.base_form.data = {
                ...defaults.data,
                comment: data.comment ?? '',
            }

            this.base_form.files = []
            this.base_form.file_previews = []
            this.base_form.errors = []
            this.base_form.sending = false
            this.base_form.successfully_sent = false
        },

        showBaseCallbackPopup(data = {}) {
            this.resetBaseFormForOpen(data)

            const themeStore = useThemeStore()
            themeStore.setBodyOverflow(true)

            this.base_form.animation_in_progress = false
            this.base_form.isOpen = true
            this.base_form.description = data.description ?? null

            setTimeout(() => {
                this.base_form.animation_in_progress = true
            }, 50)
        },

        closeCallbackPopup() {
            const themeStore = useThemeStore()
            themeStore.setBodyOverflow(false)

            this.base_form.animation_in_progress = false

            setTimeout(() => {
                this.clearBaseFormFilePreviews()
                this.base_form = getBaseFormDefaultData()
            }, 300)
        },

        sendPopupForm() {
            if (!checkDocument()) return;

            this.base_form.errors = []
            this.base_form.sending = false
            this.base_form.data.captcha_token = ''
            this.base_form.successfully_sent = false

            const captchaId = 'smart-captcha-container-base-callback'

            const existingContainer = document.getElementById(captchaId)
            if (existingContainer) {
                try {
                    window.smartCaptcha.destroy(existingContainer.dataset.widgetId)
                } catch (e) {
                }
                existingContainer.remove()
            }

            const container = document.createElement('div')
            container.id = captchaId
            container.style.position = 'absolute'
            container.style.zIndex = '-1'
            document.body.appendChild(container)

            let tokenReceived = false

            const widgetId = window.smartCaptcha.render(captchaId, {
                sitekey: import.meta.env.VITE_YANDEX_SMARTCAPTCHA_SECRET,
                invisible: true,
                hideShield: true,
                callback: (token) => {
                    tokenReceived = true
                    this.base_form.sending = true
                    this.base_form.data.captcha_token = token

                    const formData = new FormData()
                    formData.append('bitrix_title', this.base_form.bitrix_title)
                    formData.append('full_name', this.base_form.data.full_name || '')
                    formData.append('phone', this.base_form.data.phone || '')
                    formData.append('captcha_token', this.base_form.data.captcha_token || '')
                    formData.append('comment', this.base_form.data.comment || '')
                    formData.append('description', this.base_form.description || '')

                    this.base_form.files.forEach((file, index) => {
                        formData.append(`files[${index}]`, file)
                    })

                    axios.post('/axios/form/send-lead', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }).then(() => {
                        this.base_form.sending = false
                        this.base_form.successfully_sent = true
                        this.base_form.data.captcha_token = ''

                        try {
                            window.smartCaptcha.destroy(widgetId)
                        } catch (e) {
                        }
                    }).catch(error => {
                        this.base_form.sending = false

                        if (error.response?.status === 422) {
                            this.base_form.errors = error.response.data.errors
                        } else {
                            this.base_form.errors = {
                                common: ['Не удалось отправить форму. Попробуйте ещё раз.']
                            }
                        }
                    })
                }
            })

            const captchaContainer = document.getElementById(captchaId)
            if (captchaContainer) {
                captchaContainer.dataset.widgetId = widgetId
            }

            window.smartCaptcha.subscribe(widgetId, 'challenge-hidden', () => {
                if (!tokenReceived) {
                    this.base_form.sending = false
                    this.base_form.errors = {
                        captcha_token: ['Вы закрыли капчу. Повторите попытку.']
                    }

                    try {
                        window.smartCaptcha.reset(widgetId)
                    } catch (e) {
                    }
                }
            })

            setTimeout(() => {
                try {
                    window.smartCaptcha.execute(widgetId)
                } catch (e) {
                    this.base_form.sending = false
                    this.base_form.errors = {
                        captcha_token: ['Не удалось запустить капчу. Попробуйте ещё раз.']
                    }
                }
            }, 100)
        }
    }
});
